import { useState, useEffect, useCallback } from 'react';
import { aporteExternoCuotaService, ResumenCuotaEntidad } from '../services/aporteExternoCuotaService';
import { Select } from '../components/ui/Select';
import { useAuth } from '../context/AuthContext';
import { getZonalAccess } from '../utils/permissions';
import { formatCurrency } from '../utils/formatters';
import { useProvinciasDisponibles } from '../hooks/useProvinciasDisponibles';
import { useDiocesisDisponibles } from '../hooks/useDiocesisDisponibles';
import {
  Banknote,
  Building2,
  TrendingUp,
  ArrowDownToLine,
  Clock,
  AlertCircle,
} from 'lucide-react';

export default function AdminCuotaIglesiaPage() {
  const { usuario } = useAuth();
  const zonalAccess = getZonalAccess(usuario);

  const rolNombre = usuario?.rol?.nombre || '';
  const isDiocesano = rolNombre === 'admin_diocesano';
  const isNacional = rolNombre === 'admin_nacional';
  const isGlobal = rolNombre === 'admin_global';

  const hoy = new Date();
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [loading, setLoading] = useState(true);
  const [entidades, setEntidades] = useState<ResumenCuotaEntidad[]>([]);
  // Estado para selector de nivel operacional (solo admin_global)
  const [nivelOperacional, setNivelOperacional] = useState<'nacional' | 'diocesano'>('nacional');
  
  // Hooks para provincias y diócesis (solo admin_global)
  const { 
    provincias: provinciasDisponibles, 
    provinciaSeleccionada, 
    setProvinciaSeleccionada 
  } = useProvinciasDisponibles();
  
  const { 
    diocesis: diocesisDisponibles, 
    diocesisSeleccionada, 
    setDiocesisSeleccionada 
  } = useDiocesisDisponibles();

  // Función para obtener entidad destino según el rol y nivel
  const getEntidadDestino = () => {
    if (isDiocesano) {
      return {
        id: zonalAccess.diocesisId || '',
        nombre: 'Diócesis',
        tipo: 'diocesis' as const
      };
    } else if (isNacional) {
      return {
        id: zonalAccess.provinciaEclesiasticaId || '',
        nombre: 'Provincia Eclesiástica',
        tipo: 'provincia' as const
      };
    } else if (isGlobal) {
      return nivelOperacional === 'nacional'
        ? {
            id: provinciaSeleccionada || '',
            nombre: 'Provincia Eclesiástica',
            tipo: 'provincia' as const
          }
        : {
            id: diocesisSeleccionada || '',
            nombre: 'Diócesis',
            tipo: 'diocesis' as const
          };
    }
    return { id: '', nombre: '', tipo: 'diocesis' as const };
  };

  const entidadDestino = getEntidadDestino();
  const entidadDestinoId = entidadDestino.id;
  const entidadDestinoNombre = entidadDestino.nombre;
  const tipoSubordinada = (isDiocesano || (isGlobal && nivelOperacional === 'diocesano')) ? 'Lumisiales' : 'Diócesis';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      let params = {};
      if (isDiocesano) {
        params = { diocesisId: zonalAccess.diocesisId || undefined };
      } else if (isNacional) {
        params = { provinciaEclesiasticaId: zonalAccess.provinciaEclesiasticaId || undefined };
      } else if (isGlobal) {
        params = nivelOperacional === 'nacional'
          ? { provinciaEclesiasticaId: provinciaSeleccionada || undefined }
          : { diocesisId: diocesisSeleccionada || undefined };
      }
      
      const data = await aporteExternoCuotaService.getResumenPorEntidades({
        ...params,
        mes: mes || undefined,
        anio,
      });
      setEntidades(data);
    } catch (error) {
      console.error('Error cargando resumen de cuota iglesia:', error);
    } finally {
      setLoading(false);
    }
  }, [isDiocesano, isNacional, isGlobal, nivelOperacional, provinciaSeleccionada, diocesisSeleccionada, zonalAccess.diocesisId, zonalAccess.provinciaEclesiasticaId, mes, anio]);

  useEffect(() => {
    if (isDiocesano || isNacional || isGlobal) {
      loadData();
    }
  }, [loadData, isDiocesano, isNacional, isGlobal]);

  // Totales
  const totalRecaudado = entidades.reduce((s, e) => s + e.total_recaudado, 0);
  const totalTransferido = entidades.reduce((s, e) => s + e.total_transferido, 0);
  const totalPendiente = entidades.reduce((s, e) => s + e.saldo_pendiente, 0);

  // Si el usuario no es diocesano, nacional ni global, mostrar acceso denegado
  if (!isDiocesano && !isNacional && !isGlobal) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <AlertCircle className="w-12 h-12 mb-3 text-slate-300" />
        <p className="font-medium text-lg">Acceso no disponible</p>
        <p className="text-sm mt-1">Esta sección es exclusiva para admin diocesano, nacional y global.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-1">Cuota Iglesia</h1>
        <p className="text-slate-500">
          Resumen de cuota iglesia por {tipoSubordinada.toLowerCase()} — {entidadDestinoNombre}
        </p>
      </div>

      {/* Panel de selectores para admin_global */}
      {isGlobal && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Nivel Operacional"
              options={[
                { value: 'nacional', label: 'Nivel Nacional (Provincia → Diócesis)' },
                { value: 'diocesano', label: 'Nivel Diocesano (Diócesis → Lumisiales)' },
              ]}
              value={nivelOperacional}
              onChange={(v) => setNivelOperacional(v as 'nacional' | 'diocesano')}
              placeholder=""
            />

            {nivelOperacional === 'nacional' ? (
              <Select
                label="Provincia Eclesiástica"
                options={provinciasDisponibles.map((p) => ({ value: p.id, label: p.nombre }))}
                value={provinciaSeleccionada}
                onChange={(v) => setProvinciaSeleccionada(v)}
                placeholder={provinciasDisponibles.length === 0 ? 'Cargando...' : 'Seleccionar...'}
                disabled={provinciasDisponibles.length === 0}
              />
            ) : (
              <Select
                label="Diócesis"
                options={diocesisDisponibles.map((d) => ({ value: d.id, label: d.nombre }))}
                value={diocesisSeleccionada}
                onChange={(v) => setDiocesisSeleccionada(v)}
                placeholder={diocesisDisponibles.length === 0 ? 'Cargando...' : 'Seleccionar...'}
                disabled={diocesisDisponibles.length === 0}
              />
            )}
          </div>
        </div>
      )}

      {/* Filtros de período */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Mes"
            options={[
              { value: '0', label: 'Todo el año' },
              ...['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((nombre, i) => ({
                value: String(i + 1),
                label: nombre,
              })),
            ]}
            value={String(mes)}
            onChange={(v) => setMes(Number(v))}
            placeholder="Todo el año"
          />
          <Select
            label="Año"
            options={Array.from({ length: hoy.getFullYear() - 2023 }, (_, i) => 2024 + i).map(y => ({
              value: String(y),
              label: String(y),
            }))}
            value={String(anio)}
            onChange={(v) => setAnio(Number(v))}
            placeholder=""
          />
        </div>
      </div>

      {/* Indicadores resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            <p className="text-sm text-slate-500">Total Recaudado</p>
          </div>
          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalRecaudado)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <ArrowDownToLine className="w-5 h-5 text-blue-500" />
            <p className="text-sm text-slate-500">Total Transferido</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalTransferido)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-amber-500" />
            <p className="text-sm text-slate-500">Saldo Pendiente</p>
          </div>
          <p className="text-2xl font-bold text-amber-700">{formatCurrency(totalPendiente)}</p>
        </div>
      </div>

      {/* Tabla de entidades */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-4">Detalle por {tipoSubordinada}</h2>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          )}
          {!loading && isGlobal && !entidadDestinoId && (
            <div className="text-center py-16 px-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 inline-block">
                <Banknote className="w-10 h-10 mx-auto mb-2 text-blue-400" />
                <p className="font-medium text-blue-700">
                  Selecciona {nivelOperacional === 'nacional' ? 'una provincia eclesiástica' : 'una diócesis'}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  Elige {nivelOperacional === 'nacional' ? 'una provincia' : 'una diócesis'} para ver el resumen de cuota iglesia
                </p>
              </div>
            </div>
          )}
          {!loading && (!isGlobal || entidadDestinoId) && entidades.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Banknote className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No se encontraron {tipoSubordinada.toLowerCase()}</p>
              {isGlobal && (
                <p className="text-sm mt-1">
                  No hay datos disponibles para {nivelOperacional === 'nacional' ? 'la provincia' : 'la diócesis'} seleccionada
                </p>
              )}
            </div>
          )}
          {!loading && entidades.length > 0 && (
            <div className="overflow-x-auto">
              {/* Encabezado */}
              <div className="grid grid-cols-12 gap-2 px-5 py-3 text-xs text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <div className="col-span-4">{isDiocesano ? 'Lumisial' : 'Diócesis'}</div>
                <div className="col-span-3 text-right">Recaudado</div>
                <div className="col-span-3 text-right">Transferido</div>
                <div className="col-span-2 text-right">Pendiente</div>
              </div>

              {/* Filas */}
              {entidades.map((entidad) => (
                <div
                  key={entidad.entidad_id}
                  className="grid grid-cols-12 gap-2 px-5 py-4 items-center border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
                >
                  <div className="col-span-4 flex items-center gap-2 min-w-0">
                    <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700 truncate">
                      {entidad.entidad_nombre}
                    </span>
                  </div>
                  <div className="col-span-3 text-right">
                    <span className="text-sm text-emerald-600 font-medium">
                      {formatCurrency(entidad.total_recaudado)}
                    </span>
                  </div>
                  <div className="col-span-3 text-right">
                    <span className="text-sm text-blue-600 font-medium">
                      {formatCurrency(entidad.total_transferido)}
                    </span>
                  </div>
                  <div className="col-span-2 text-right">
                    <span className={`text-sm font-medium ${entidad.saldo_pendiente > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                      {formatCurrency(entidad.saldo_pendiente)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
